'use client';

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { EditorInstance } from 'novel';
import { defaultExtensions } from '@/components/markdown/extensions';
import { EditorCore, EditorCoreRef } from '@/components/markdown/components/EditorCore';
import { ChatMessageList, ChatInputArea, ConnectionStatus, ConnectionState, ChatMessage, useAudioRecorder, useChatConfig } from '@contentgrowth/llm-service/ui/react';
import AgentChatPanel, { AgentChatPanelRef } from '@/app/articles/components/AgentChatPanel';
import { useChat } from '@/hooks/useChat';
import { articlesApi } from '@/app/api/articles';
import { getFormattedContent, countWords } from '@/components/markdown/utils/content-formatter';
import { marked } from 'marked';
import { generateJSON } from '@tiptap/html';
import { configureMarkedWithImageSize } from '@/components/markdown/extensions/image-size-extension';
import { JSONContent } from '@tiptap/react';
import { CommandShortcut } from '@/components/markdown/extensions/command-shortcut';
import { ChangeIndicator } from '@/components/markdown/extensions/change-indicator';
import { useImageUpload } from '@/components/markdown/hooks/useImageUpload';
import { useAICommandManager } from '@/components/markdown/hooks/useAICommandManager';
import { suggestionItems } from '@/components/markdown/components/slash-command';
import AICommandDialog from '@/components/markdown/components/ai-command-dialog';
import AssetBrowserModal from '@/components/assets/AssetBrowserModal';
import {
    ArrowLeftIcon,
    ChatBubbleLeftRightIcon,
    ArrowDownTrayIcon,
    ExclamationCircleIcon,
    MicrophoneIcon
} from '@heroicons/react/24/outline';
import {
    Bold, Italic, Underline, Strikethrough, Code,
    List, ListOrdered, CheckSquare,
    Link as LinkIcon, Image as ImageIcon, Quote, Table as TableIcon,
    Undo, Redo, Sparkles, Type, Heading1, Heading2, Heading3, Pilcrow,
    ChevronLeft, Save, MoreHorizontal, Check, LogOut, Pencil, X,
    PanelRightClose, PanelRightOpen, FileDiff, Keyboard, KeyboardOff, Delete, CornerDownLeft, Space
} from 'lucide-react';
import { LinkDialog } from '@/components/markdown/components/link-dialog';
import DiffViewer from '@/app/articles/components/DiffViewer';

// Props
interface MobileAgentEditorProps {
    articleId?: string;
    articleTitle?: string;
    initialContent?: string;
    onSave?: (content: string) => void;
    onBlur?: (content: string) => void;
    onCancel?: () => void;
    onAiEdit?: (instruction: string, messages?: ChatMessage[], selection?: any, cursorContext?: any, signal?: AbortSignal) => Promise<{ content: string; message: string; hasContentChanges: boolean; oldContent: string; }>;
    onContentUpdate?: (newContent: string, previousContent?: string, changeSummary?: string) => void;
    previousContent?: string;
    isAiProcessing?: boolean;
    canUndo?: boolean;
    canRedo?: boolean;
    lastChangeSummary?: string;
    nextChangeSummary?: string;
    onUndo?: () => void;
    onRedo?: () => void;
    onReset?: () => void;
    manualEdit?: { oldContent: string; newContent: string } | null;
    editorCoreRef?: React.RefObject<EditorCoreRef>;
    format?: 'markdown' | 'html' | 'json';
    onUnsavedChange?: (hasUnsaved: boolean) => void;
    onTitleChange?: (newTitle: string) => void;
}

export default function MobileAgentEditor({
    articleId,
    articleTitle,
    initialContent = '',
    onSave,
    onBlur,
    onCancel,
    onAiEdit,
    onContentUpdate,
    previousContent = '',
    isAiProcessing = false,
    canUndo,
    canRedo,
    lastChangeSummary,
    nextChangeSummary,
    onUndo,
    onRedo,
    onReset,
    manualEdit,
    editorCoreRef,
    format = 'markdown',
    onUnsavedChange,
    onTitleChange
}: MobileAgentEditorProps) {
    const navigate = useNavigate();
    // UI State
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isSidebarHidden, setIsSidebarHidden] = useState(false);
    const [isAssetBrowserOpen, setIsAssetBrowserOpen] = useState(false);
    const [saveStatus, setSaveStatus] = useState('Saved');
    const [wordCount, setWordCount] = useState(0);
    const [isTitleEditing, setIsTitleEditing] = useState(false);
    const [tempTitle, setTempTitle] = useState(articleTitle || '');
    const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
    const [linkData, setLinkData] = useState<{ url: string, text?: string, isEditing: boolean }>({ url: '', isEditing: false });

    const isCancellingVoiceRef = useRef(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [voiceError, setVoiceError] = useState<string | null>(null);
    const recordingStartTimeRef = useRef<number | null>(null);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const MAX_RECORDING_SECONDS = 90;
    const [remainingSeconds, setRemainingSeconds] = useState(MAX_RECORDING_SECONDS);

    // Keyboard Toggle State
    const [isKeyboardEnabled, setIsKeyboardEnabled] = useState(false);
    const isKeyboardEnabledRef = useRef(false);

    // Voice Input State
    const [isVoiceActive, setIsVoiceActive] = useState(false);
    const { voice: globalVoice } = useChatConfig();
    const wrappedVoiceConfig = useMemo(() => {
        const baseConfig = globalVoice?.config;
        return {
            ...baseConfig,
            mode: 'custom' as const,
            onVoiceStart: () => {
                setIsVoiceActive(true);
                baseConfig?.onVoiceStart?.();
            },
            onVoiceEnd: () => {
                setIsVoiceActive(false);
                baseConfig?.onVoiceEnd?.();
            },
            onAudioCapture: baseConfig?.onAudioCapture
        };
    }, [globalVoice?.config]);

    // Stable ref so the async recorder callback always reads latest routing state
    const voiceResultRouterRef = useRef<(text: string) => void>(() => {});
    // Keep isKeyboardEnabledRef in sync every render
    isKeyboardEnabledRef.current = isKeyboardEnabled;
    // Re-assigned every render — always points to current isChatOpen / refs
    voiceResultRouterRef.current = (text: string) => {
        if (isChatOpen) {
            chatPanelRef.current?.replaceSelection(text);
        } else {
            // Re-assert inputmode=none BEFORE focus+insert to prevent keyboard popup
            if (!isKeyboardEnabledRef.current && editorInstance) {
                (editorInstance.view.dom as HTMLElement).setAttribute('inputmode', 'none');
            }
            appliedEditorRef?.current?.insertContent(text);
        }
    };

    // Keep voiceConfig fresh inside async callback
    const voiceConfigRef = useRef(wrappedVoiceConfig);
    voiceConfigRef.current = wrappedVoiceConfig;

    // Direct recorder — no TapToTalk dependency, full cancel/finish control
    const audioRecorder = useAudioRecorder(async (blob) => {
        const cancelled = isCancellingVoiceRef.current;
        isCancellingVoiceRef.current = false;
        setIsVoiceActive(false);
        console.log('MobileAgentEditor: audioRecorder callback', { cancelled, hasBlob: !!blob });
        if (cancelled || !blob) return;
        setIsTranscribing(true);
        try {
            const capture = voiceConfigRef.current?.onAudioCapture;
            if (capture) {
                console.log('MobileAgentEditor: starting transcription');
                const text = await capture(blob);
                console.log('MobileAgentEditor: transcription result', { text: text?.slice(0, 100) });
                if (text) voiceResultRouterRef.current(text);
                
                // Restore focus only when keyboard is enabled; editor is already
                // focused by insertContent's internal chain().focus() otherwise.
                setTimeout(() => {
                    if (isChatOpen) {
                        chatPanelRef.current?.focus();
                    } else if (editorInstance && isKeyboardEnabledRef.current) {
                        editorInstance.commands.focus();
                    }
                }, 50);
            }
        } catch (e) {
            console.error('MobileAgentEditor: transcription error', e);
            setVoiceError((e as Error)?.message || 'Transcription failed');
        } finally {
            setIsTranscribing(false);
        }
    });

    const handleStartRecording = useCallback(async () => {
        setVoiceError(null);
        setElapsedSeconds(0);
        setRemainingSeconds(MAX_RECORDING_SECONDS);
        recordingStartTimeRef.current = Date.now();
        try {
            await audioRecorder.start();
            setIsVoiceActive(true);
        } catch (e) {
            recordingStartTimeRef.current = null;
            setVoiceError((e as Error)?.message || 'Mic access denied');
        }
    }, [audioRecorder]);

    const handleCancelVoice = useCallback(() => {
        isCancellingVoiceRef.current = true;
        audioRecorder.cancel();
        setIsVoiceActive(false);
        setIsTranscribing(false);
        setVoiceError(null);
    }, [audioRecorder]);

    const handleFinishVoice = useCallback(() => {
        isCancellingVoiceRef.current = false;
        audioRecorder.stop();
        // onStop callback handles: setIsVoiceActive(false), transcription, routing
    }, [audioRecorder]);

    // Tick elapsed time while actively recording and enforce 90s limit
    useEffect(() => {
        if (!isVoiceActive) return;
        let interval: NodeJS.Timeout;
        let hasAutoFinished = false;
        interval = setInterval(() => {
            if (recordingStartTimeRef.current !== null && !hasAutoFinished) {
                const elapsed = (Date.now() - recordingStartTimeRef.current) / 1000;
                setElapsedSeconds(elapsed);
                const remaining = Math.max(0, MAX_RECORDING_SECONDS - elapsed);
                setRemainingSeconds(remaining);
                // Auto-finish when limit reached (once only)
                if (remaining <= 0) {
                    hasAutoFinished = true;
                    console.log('MobileAgentEditor: 90s auto-finish triggered');
                    handleFinishVoice();
                    clearInterval(interval);
                }
            }
        }, 100);
        return () => clearInterval(interval);
    }, [isVoiceActive, handleFinishVoice]);

    const [editorInstance, setEditorInstance] = useState<EditorInstance | null>(null);

    // One-shot: focus editor when it first mounts (inputmode=none suppresses keyboard).
    const initialFocusDoneRef = useRef(false);
    useEffect(() => {
        if (!editorInstance || initialFocusDoneRef.current || isChatOpen) return;
        initialFocusDoneRef.current = true;
        const timeout = setTimeout(() => {
            editorInstance.commands.focus();
        }, 300); // 300ms gives EditorCore time to fully paint
        return () => clearTimeout(timeout);
    }, [editorInstance]); // intentionally omit isChatOpen — one-shot only

    // Explicit focus routing whenever the user switches chat on/off.
    const prevIsChatOpenRef = useRef(isChatOpen);
    useEffect(() => {
        if (prevIsChatOpenRef.current === isChatOpen) return;
        prevIsChatOpenRef.current = isChatOpen;
        const timeout = setTimeout(() => {
            if (isChatOpen) {
                chatPanelRef.current?.focus();
            } else if (editorInstance) {
                editorInstance.commands.focus();
            }
        }, 100);
        return () => clearTimeout(timeout);
    }, [isChatOpen, editorInstance]);

    const [isDiffViewerOpen, setIsDiffViewerOpen] = useState(false);

    // Toolbar Logic
    const [activeMenu, setActiveMenu] = useState<{ type: 'type' | 'format' | 'list' | 'more', rect: DOMRect } | null>(null);

    const toggleMenu = (e: React.MouseEvent<HTMLButtonElement>, menu: 'type' | 'format' | 'list' | 'more') => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setActiveMenu(activeMenu?.type === menu ? null : { type: menu, rect });
    };
    const activateChangeIndicatorsRef = useRef<(() => void) | null>(null);

    // --- Visual Viewport Handling for Mobile Keyboard ---
    const [viewportHeight, setViewportHeight] = useState<number | null>(null);

    useEffect(() => {
        const handleResize = () => {
            if (window.visualViewport) {
                setViewportHeight(window.visualViewport.height);
            }
        };

        if (window.visualViewport) {
            setViewportHeight(window.visualViewport.height);
            window.visualViewport.addEventListener('resize', handleResize);
            window.visualViewport.addEventListener('scroll', handleResize); // Sometimes scroll affects it
        }

        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handleResize);
                window.visualViewport.removeEventListener('scroll', handleResize);
            }
        };
    }, []);



    // Link Handlers (Replicated from FixedToolbar)

    // Link Handlers (Replicated from FixedToolbar)
    const handleOpenLinkDialog = useCallback(() => {
        if (!editorInstance) return;
        const { state } = editorInstance;
        const { from, to, $from } = state.selection;
        const link = editorInstance.getAttributes('link');

        if (link.href) {
            // Editing existing link logic
            let linkStart = from;
            let linkEnd = to;
            let foundLinkAt = -1;

            // Simplified link range finding (robust fallback)
            // Check current and adjacent positions
            for (let offset = 0; offset <= 1; offset++) {
                const checkPos = Math.min(from + offset, state.doc.content.size);
                if (checkPos > 0) {
                    const resolved = state.doc.resolve(checkPos);
                    const marks = resolved.marks();
                    if (marks.some(m => m.type.name === 'link' && m.attrs.href === link.href)) {
                        foundLinkAt = checkPos;
                        break;
                    }
                }
            }

            if (foundLinkAt >= 0) {
                // Scan backwards
                for (let i = foundLinkAt; i >= 0; i--) {
                    const pos = state.doc.resolve(i);
                    if (!pos.marks().some(m => m.type.name === 'link' && m.attrs.href === link.href)) {
                        linkStart = i;
                        break;
                    }
                    if (i === 0) linkStart = 0;
                }
                // Scan forwards
                for (let i = foundLinkAt; i <= state.doc.content.size; i++) {
                    const pos = state.doc.resolve(i);
                    if (!pos.marks().some(m => m.type.name === 'link' && m.attrs.href === link.href)) {
                        linkEnd = i - 1;
                        break;
                    }
                    if (i === state.doc.content.size) linkEnd = i;
                }
                const linkText = state.doc.textBetween(linkStart, linkEnd);
                setLinkData({ url: link.href, text: linkText, isEditing: true });
            } else {
                setLinkData({ url: link.href, text: '', isEditing: true });
            }
        } else {
            // New link
            let selectedText = state.doc.textBetween(from, to);
            selectedText = selectedText.trim();
            setLinkData({ url: '', text: selectedText, isEditing: false });
        }
        setIsLinkDialogOpen(true);
    }, [editorInstance]);

    const handleSaveLink = useCallback((url: string, text?: string) => {
        if (!editorInstance) return;
        if (linkData.isEditing) {
            if (text && text !== linkData.text) {
                editorInstance.chain().focus().extendMarkRange('link').deleteSelection().insertContent(`<a href="${url}">${text}</a>`).run();
            } else {
                editorInstance.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
            }
        } else {
            const { state } = editorInstance;
            let { from, to } = state.selection;
            if (from !== to) {
                editorInstance.chain().focus().setLink({ href: url }).run();
            } else if (text) {
                editorInstance.chain().focus().insertContent(`<a href="${url}">${text}</a>`).run();
            } else {
                editorInstance.chain().focus().insertContent(`<a href="${url}">${url}</a>`).run();
            }
        }
    }, [editorInstance, linkData]);

    const handleRemoveLink = useCallback(() => {
        editorInstance?.chain().focus().unsetLink().run();
    }, [editorInstance]);

    // Listen for custom event to open link dialog (e.g. from CustomLink extension)
    useEffect(() => {
        const handleOpenLinkEvent = () => {
            handleOpenLinkDialog();
        };
        document.addEventListener('open-link-dialog', handleOpenLinkEvent);
        return () => {
            document.removeEventListener('open-link-dialog', handleOpenLinkEvent);
        };
    }, [handleOpenLinkDialog]);

    // Refs
    const localEditorRef = useRef<EditorCoreRef>(null);
    const appliedEditorRef = editorCoreRef || localEditorRef;
    const contentUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const chatPanelRef = useRef<AgentChatPanelRef>(null);

    // --- Helpers ---
    const getInitialContent = useCallback((): JSONContent => {
        if (!initialContent || (typeof initialContent === 'string' && initialContent.trim() === '')) {
            return { type: 'doc', content: [{ type: 'paragraph' }] };
        }
        if (format === 'markdown') {
            try {
                configureMarkedWithImageSize();
                const html = marked.parse(initialContent as string) as string;
                return generateJSON(html, defaultExtensions);
            } catch (error) {
                console.error('Error converting markdown to JSON:', error);
            }
        }
        return { type: 'doc', content: [{ type: 'paragraph', content: [{ type: 'text', text: initialContent as string }] }] };
    }, [initialContent, format]);

    const getExtensions = useCallback(() => {
        const extensions = [...defaultExtensions, CommandShortcut];
        extensions.push(
            ChangeIndicator.configure({
                previousContent,
                enabled: true,
            }) as any
        );
        return extensions;
    }, [previousContent]);

    // Image Upload
    const { isUploadingImage, handleAssetSelect, handleImagePaste } = useImageUpload({ editorInstance });

    // AI Command
    const {
        aiDialogOpen,
        aiCommandType,
        aiService,
        openAiCommandDialog,
        closeAiCommandDialog,
        handleAiCommandSubmit,
        handleAcceptResult
    } = useAICommandManager(editorInstance, articleTitle);

    const isAnyModalOpen =
        isLinkDialogOpen ||
        isAssetBrowserOpen ||
        aiDialogOpen ||
        isDiffViewerOpen ||
        isTitleEditing;

    // Suppress mobile keyboard via inputmode attribute on the editor DOM element.
    // inputmode="none" prevents virtual keyboard while keeping focus/caret positioning.
    useEffect(() => {
        if (!editorInstance) return;

        // When a modal dialog is open (e.g. link dialog), don't fight the browser's focus.
        // Use native dom.blur() — ProseMirror commands.blur() doesn't fully release the
        // mobile browser's active-input context, so keystrokes still route to the editor.
        if (isAnyModalOpen) {
            const dom = editorInstance.view.dom as HTMLElement;
            dom.removeAttribute('inputmode');
            dom.blur(); // native blur: fully hands off keyboard routing to the dialog
            return;
        }

        // If keyboard was just disabled while editor is focused, blur and refocus to dismiss it immediately.
        // The actual inputmode="none" is now handled reactively by EditorCore via the inputMode prop.
        if (!isKeyboardEnabled && editorInstance.isFocused) {
            editorInstance.commands.blur();
            requestAnimationFrame(() => editorInstance.commands.focus());
        }
    }, [editorInstance, isKeyboardEnabled, isAnyModalOpen]);

    // Haptic + visual key feedback
    const triggerKeyFeedback = useCallback(() => {
        if (typeof navigator !== 'undefined' && navigator.vibrate) {
            navigator.vibrate(8);
        }
    }, []);

    // Stable refs so handlers can call chat fns declared later in the file
    const chatInputRef = useRef('');
    const chatSubmitRef = useRef<((e: React.FormEvent) => void) | null>(null);
    const chatInputChangeRef = useRef<((e: React.ChangeEvent<HTMLTextAreaElement>) => void) | null>(null);

    // --- Delete handler (used by mobile delete button) ---
    const handleDeleteInEditor = useCallback(() => {
        if (isChatOpen) {
            chatInputChangeRef.current?.({ target: { value: chatInputRef.current.slice(0, -1) } } as React.ChangeEvent<HTMLTextAreaElement>);
            return;
        }
        if (!editorInstance) return;
        const { state } = editorInstance;
        const { from, to, empty } = state.selection;
        if (!empty) {
            editorInstance.chain().focus().deleteSelection().run();
            return;
        }
        if (from <= 1) return;
        const $from = state.doc.resolve(from);
        if ($from.parentOffset > 0) {
            // Inside text — delete one character before cursor
            editorInstance.view.dispatch(state.tr.delete(from - 1, from));
        } else {
            // At block start — join with previous block
            editorInstance.chain().focus().joinBackward().run();
        }
    }, [isChatOpen, editorInstance]);

    // --- Enter/Return handler (used by mobile return button) ---
    const handleReturnInEditor = useCallback(() => {
        if (isChatOpen) {
            chatSubmitRef.current?.({ preventDefault: () => {} } as React.FormEvent);
            return;
        }
        if (!editorInstance) return;
        editorInstance.chain().focus().enter().run();
    }, [isChatOpen, editorInstance]);

    // --- Space handler ---
    const handleSpaceInEditor = useCallback(() => {
        if (isChatOpen) {
            chatInputChangeRef.current?.({ target: { value: chatInputRef.current + ' ' } } as React.ChangeEvent<HTMLTextAreaElement>);
            return;
        }
        if (!editorInstance) return;
        editorInstance.view.dispatch(editorInstance.state.tr.insertText(' '));
    }, [isChatOpen, editorInstance]);

    // --- Chat Logic ---
    const handleChatSubmit = useCallback(async (currentMessages: ChatMessage[], signal?: AbortSignal) => {
        const lastMessage = currentMessages[currentMessages.length - 1];
        if (!lastMessage || lastMessage.role !== 'user' || !onAiEdit) return null;

        // Close chat panel when submitting - user sees the button loading state
        setIsChatOpen(false);

        return onAiEdit(
            lastMessage.content,
            currentMessages.slice(0, -1),
            appliedEditorRef?.current?.getSelectionContext(),
            appliedEditorRef?.current?.getCursorContext(),
            signal
        );
    }, [onAiEdit, appliedEditorRef]);

    const handleChatResponse = useCallback((response: any) => {
        const { content: newContent, message, oldContent, hasContentChanges, changeSummary, rawContent } = response;
        const partialMessage: Partial<ChatMessage> = { content: message };
        if (rawContent) partialMessage.rawContent = rawContent;
        if (hasContentChanges && newContent && onContentUpdate) {
            onContentUpdate(newContent, oldContent, changeSummary);
            if (oldContent) partialMessage.diff = { oldContent, newContent };
        }
        return partialMessage;
    }, [onContentUpdate]);

    const {
        messages: chatMessages,
        setMessages: setChatMessages,
        input: chatInput,
        handleInputChange: handleChatInputChange,
        handleSubmit: handleChatSubmitForm,
        isLoading: isChatLoading,
        error: chatError,
        sendMessage: chatSendMessage,
        stop: chatStop
    } = useChat({
        handler: handleChatSubmit,
        onResponse: handleChatResponse,
        enabled: true
    });

    // Keep stable refs fresh every render so delete/return handlers can use them
    chatInputRef.current = chatInput;
    chatSubmitRef.current = handleChatSubmitForm;
    chatInputChangeRef.current = handleChatInputChange;

    // Inject manual edits into chat
    const lastProcessedEditRef = useRef(manualEdit);
    useEffect(() => {
        if (manualEdit && manualEdit !== lastProcessedEditRef.current) {
            const manualEditMessage: ChatMessage = {
                id: `manual-edit-${Date.now()}`,
                role: 'system',
                content: 'Manual edit detected.',
                diff: manualEdit,
            };
            setChatMessages(prev => [...prev, manualEditMessage]);
            lastProcessedEditRef.current = manualEdit;
        }
    }, [manualEdit, setChatMessages]);

    // --- Save Logic ---
    const handleContentSave = useCallback(async (blurring: boolean = false) => {
        const unsaved = appliedEditorRef?.current?.hasUnsavedChanges();
        if (!editorInstance || !unsaved) return;

        const currentContent = getFormattedContent(editorInstance, format) as string;

        if (onBlur) onBlur(currentContent);

        try {
            setSaveStatus('Saving...');
            await articlesApi.saveContent(articleId || '', currentContent);
            appliedEditorRef?.current?.clearUnsavedChanges();
            if (onUnsavedChange) onUnsavedChange(false);
            setSaveStatus('Saved');
        } catch (error) {
            setSaveStatus('Save failed');
        }
    }, [editorInstance, onBlur, format, articleId, appliedEditorRef, onUnsavedChange]);

    // Shared key-repeat handler factory that returns stable handlers
    const keyRepeatHandlersRef = useRef(new Map<() => void, {
        onPointerDown: (e: React.PointerEvent) => void;
        onPointerUp: () => void;
        onPointerLeave: () => void;
        onPointerCancel: () => void;
    }>());
    const getKeyRepeatHandlers = useCallback((action: () => void) => {
        // Return existing stable handlers if already created for this action
        if (keyRepeatHandlersRef.current.has(action)) {
            return keyRepeatHandlersRef.current.get(action)!;
        }
        
        const timer = { id: null as ReturnType<typeof setTimeout> | null };
        const interval = { id: null as ReturnType<typeof setInterval> | null };
        const clear = () => {
            if (timer.id !== null) { clearTimeout(timer.id); timer.id = null; }
            if (interval.id !== null) { clearInterval(interval.id); interval.id = null; }
        };
        
        const handlers = {
            onPointerDown: (e: React.PointerEvent) => {
                e.preventDefault();
                triggerKeyFeedback();
                action();
                timer.id = setTimeout(() => {
                    interval.id = setInterval(() => {
                        triggerKeyFeedback();
                        action();
                    }, 80);
                }, 400);
            },
            onPointerUp: clear,
            onPointerLeave: clear,
            onPointerCancel: clear,
        };
        
        // Store and return the stable handlers
        keyRepeatHandlersRef.current.set(action, handlers);
        return handlers;
    }, [triggerKeyFeedback]);

    // --- Render ---
    return (
        <div
            className="flex flex-col bg-gray-50 fixed left-0 right-0 top-0 z-50 overflow-hidden"
            style={{
                height: viewportHeight ? `${viewportHeight}px` : '100%',
                maxHeight: '-webkit-fill-available'
            }}
        >
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shrink-0">
                <button
                    onClick={() => appliedEditorRef?.current?.hasUnsavedChanges() ? handleContentSave(false) : navigate('/articles')}
                    className={`p-1.5 rounded-lg transition-all shrink-0 ${saveStatus === 'Saving...' ? 'bg-orange-50 text-orange-600' :
                        appliedEditorRef?.current?.hasUnsavedChanges() ? 'bg-orange-50 text-orange-600' : // Unsaved -> Orange
                            'text-gray-600 hover:text-gray-900 hover:bg-gray-100' // Saved -> Gray (Exit)
                        }`}
                    title={appliedEditorRef?.current?.hasUnsavedChanges() ? 'Save Changes' : 'Exit to Articles'}
                >
                    {saveStatus === 'Saving...' ? (
                        <div className="animate-spin h-5 w-5 border-2 border-current border-t-transparent rounded-full" />
                    ) : appliedEditorRef?.current?.hasUnsavedChanges() ? (
                        <Save className="h-6 w-6" />
                    ) : (
                        <LogOut className="h-6 w-6 -scale-x-100" />
                    )}
                </button>
                <h1 className="text-lg font-semibold text-gray-800 truncate px-2 flex-1 text-center" onClick={() => {
                    if (onTitleChange) {
                        setTempTitle(articleTitle || '');
                        setIsTitleEditing(true);
                    }
                }}>
                    {articleTitle || 'Untitled'}
                </h1>
                <button
                    onClick={() => {
                        if (onTitleChange) {
                            setTempTitle(articleTitle || '');
                            setIsTitleEditing(true);
                        }
                    }}
                    className={`p-2 text-gray-600 hover:text-gray-900 ${!onTitleChange ? 'invisible' : ''}`}
                >
                    <Pencil className="h-5 w-5" />
                </button>
                <button
                    onClick={() => setIsSidebarHidden(!isSidebarHidden)}
                    className="p-2 text-gray-600 hover:text-gray-900 ml-2"
                    title={isSidebarHidden ? 'Show Sidebar' : 'Hide Sidebar'}
                >
                    {isSidebarHidden ? (
                        <PanelRightOpen className="h-6 w-6" />
                    ) : (
                        <PanelRightClose className="h-6 w-6" />
                    )}
                </button>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 flex overflow-hidden relative">
                {/* Editor Area - always rendered */}
                <div className="flex-1 flex flex-col overflow-hidden bg-white">
                    <div
                        className="flex-1 min-h-0 cursor-text [&_.prose]:!text-gray-900 [&_.prose_*:not(a)]:!text-gray-900 [&_.prose_a]:!text-blue-600 [&_.prose_a]:!underline [&_.prose_a:visited]:!text-purple-600 [&_.prose]:!text-justify [&_.prose]:[text-justify:inter-word] [&_.prose]:!text-pretty [&_.prose]:!leading-relaxed [&_p]:!mb-4 [&_.prose]:!text-base [&_.prose]:!font-sans [&_.prose]:!hyphens-auto [&_.prose]:!tracking-tight [&_.prose_h1]:!hyphens-auto [&_.prose_h2]:!hyphens-auto [&_.prose_h3]:!hyphens-auto [&_.prose_h4]:!hyphens-auto [&_.prose_h5]:!hyphens-auto [&_.prose_h6]:!hyphens-auto [&_.prose_h1]:!text-justify [&_.prose_h2]:!text-justify [&_.prose_h3]:!text-justify [&_.prose_h4]:!text-justify [&_.prose_h5]:!text-justify [&_.prose_h6]:!text-justify [&_.prose_h1]:[text-justify:inter-word] [&_.prose_h2]:[text-justify:inter-word] [&_.prose_h3]:[text-justify:inter-word] [&_.prose_h4]:[text-justify:inter-word] [&_.prose_h5]:[text-justify:inter-word] [&_.prose_h6]:[text-justify:inter-word]"
                        onClick={() => {
                            if (editorInstance && !editorInstance.isFocused) {
                                editorInstance.chain().focus().run();
                            }
                        }}
                    >
                        <EditorCore
                            ref={appliedEditorRef}
                            editorInstance={editorInstance}
                            setEditorInstance={setEditorInstance}
                            isReadOnly={false}
                            isAiProcessing={isAiProcessing || isChatLoading}
                            getInitialContent={getInitialContent}
                            getExtensions={getExtensions}
                            format={format}
                            handleImagePaste={handleImagePaste}
                            onContentUpdate={() => {
                                if (contentUpdateTimeoutRef.current) clearTimeout(contentUpdateTimeoutRef.current);
                                contentUpdateTimeoutRef.current = setTimeout(() => {
                                    if (editorInstance) setWordCount(countWords(editorInstance.getText()));
                                }, 300);
                            }}
                            onUnsavedChanges={(hasUnsaved) => {
                                if (onUnsavedChange) onUnsavedChange(hasUnsaved);
                            }}
                            onSaveStatusChange={setSaveStatus}
                            onAutoSave={() => handleContentSave(true)}
                            onChangeIndicatorsActivate={(activateFn) => { activateChangeIndicatorsRef.current = activateFn; }}
                            onAiEdit={onAiEdit}
                            chatPanelSendMessage={chatSendMessage}
                            suggestionItems={suggestionItems}
                            saveStatus={saveStatus}
                            wordCount={wordCount}
                            isUploadingImage={isUploadingImage}
                            isAgentMode={true}
                            onToggleAgentMode={onCancel}
                            onInsertAsset={() => setIsAssetBrowserOpen(true)}
                            onSave={() => handleContentSave(false)}
                            onBlurSave={() => handleContentSave(true)}
                            onAICommand={openAiCommandDialog}
                            previousContent={previousContent}
                            hideToolbar={true}
                            isVoiceInputActive={isVoiceActive}
                            inputMode={isKeyboardEnabled ? undefined : 'none'}
                        />
                    </div>
                </div>

                {/* Right Vertical Toolbar */}
                {!isSidebarHidden && (
                    <div className="w-12 bg-gray-50 border-l border-gray-200 flex flex-col items-center shrink-0 z-50 h-full overflow-hidden">
                        {/* Top Section: Tools (Scrollable) */}
                        <div className="flex-1 w-full flex flex-col items-center gap-2 py-3 overflow-y-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                            {/* AI Action */}
                            <button
                                onClick={() => openAiCommandDialog('generate-image')}
                                className="w-9 h-9 rounded-lg flex items-center justify-center transition-all bg-purple-50 text-purple-600 hover:bg-purple-100 border border-purple-200 shrink-0"
                                title="Generate Image (AI)"
                            >
                                <Sparkles className="h-4 w-4" />
                            </button>

                            <div className="w-6 h-px bg-gray-200 my-1 shrink-0" />

                            {/* Type Group */}
                            <div className="relative shrink-0">
                                <button
                                    onClick={(e) => toggleMenu(e, 'type')}
                                    className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all shrink-0 ${activeMenu?.type === 'type' ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-600 border border-gray-200'}`}
                                >
                                    <Type className="h-4 w-4" />
                                </button>
                            </div>

                            {/* Format Group (B) */}
                            <div className="relative shrink-0">
                                <button
                                    onClick={(e) => toggleMenu(e, 'format')}
                                    className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all shrink-0 ${activeMenu?.type === 'format' || editorInstance?.isActive('bold') || editorInstance?.isActive('italic') ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-600 border border-gray-200'}`}
                                >
                                    <Bold className="h-4 w-4" />
                                </button>
                            </div>

                            {/* List Group */}
                            <div className="relative shrink-0">
                                <button
                                    onClick={(e) => toggleMenu(e, 'list')}
                                    className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all shrink-0 ${activeMenu?.type === 'list' || editorInstance?.isActive('bulletList') || editorInstance?.isActive('orderedList') ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-600 border border-gray-200'}`}
                                >
                                    <List className="h-4 w-4" />
                                </button>
                            </div>

                            <div className="w-6 h-px bg-gray-200 my-1 shrink-0" />

                            {/* More Menu (+) */}
                            <div className="relative shrink-0">
                                <button
                                    onClick={(e) => toggleMenu(e, 'more')}
                                    className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all shrink-0 ${activeMenu?.type === 'more' ? 'bg-blue-100 text-blue-600' : 'bg-white text-gray-600 border border-gray-200'}`}
                                    title="More"
                                >
                                    <MoreHorizontal className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        {/* Bottom Section: Actions (Fixed) */}
                        <div className="shrink-0 w-full flex flex-col items-center gap-2 pb-3 pt-2 bg-gray-50 border-t border-gray-200 z-40">


                            {/* Undo/Redo */}
                            <button onClick={() => editorInstance?.chain().focus().undo().run()} className="w-9 h-9 rounded-lg bg-white text-gray-600 hover:bg-gray-100 border border-gray-200 flex items-center justify-center disabled:opacity-50 shrink-0" disabled={!editorInstance?.can().undo()} title="Undo">
                                <Undo className="h-4 w-4" />
                            </button>
                            <button onClick={() => editorInstance?.chain().focus().redo().run()} className="w-9 h-9 rounded-lg bg-white text-gray-600 hover:bg-gray-100 border border-gray-200 flex items-center justify-center disabled:opacity-50 shrink-0" disabled={!editorInstance?.can().redo()} title="Redo">
                                <Redo className="h-4 w-4" />
                            </button>

                            <div className="w-6 h-px bg-gray-200 my-1 shrink-0" />

                            {/* Diff Viewer Button - Only visible if previousContent exists */}
                            {previousContent && (
                                <button
                                    onClick={() => setIsDiffViewerOpen(true)}
                                    className="w-10 h-10 rounded-full flex items-center justify-center transition-all shrink-0 bg-orange-50 text-orange-600 hover:bg-orange-100 border border-orange-200 mb-2"
                                    title="View Changes"
                                >
                                    <FileDiff className="h-5 w-5" />
                                </button>
                            )}

                        </div>
                    </div>
                )}

                {/* Chat Panel Backdrop */}
                <div
                    className={`absolute inset-0 bg-black/30 z-30 transition-opacity duration-300 ${isChatOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
                    onClick={() => setIsChatOpen(false)}
                    style={!isSidebarHidden ? { right: '48px' } : undefined}
                />
                {/* Chat Panel - slides from right, stacks over editor */}
                <div
                    className={`absolute top-0 bottom-0 bg-white z-40 shadow-2xl flex flex-col transition-[width] duration-300 ease-out overflow-hidden ${isChatOpen ? 'w-[80%]' : 'w-0'}`}
                    style={!isSidebarHidden ? { right: '48px' } : { right: '0' }}
                >
                    <div className="flex-1 overflow-hidden">
                        <AgentChatPanel
                            ref={chatPanelRef}
                            articleId={articleId || ''}
                            chatState={{
                                messages: chatMessages,
                                setMessages: setChatMessages,
                                input: chatInput,
                                handleInputChange: handleChatInputChange,
                                handleSubmit: handleChatSubmitForm,
                                isLoading: isChatLoading,
                                error: chatError,
                                sendMessage: chatSendMessage,
                                stop: chatStop
                            }}
                            onUndoClick={onUndo || (() => { })}
                            onRedoClick={onRedo || (() => { })}
                            onResetClick={onReset || (() => { })}
                            canUndo={canUndo}
                            canRedo={canRedo}
                            lastChangeSummary={lastChangeSummary}
                            nextChangeSummary={nextChangeSummary}
                            inputMode={isKeyboardEnabled ? undefined : 'none'}
                            disableAutoFocus={!isChatOpen || isLinkDialogOpen || isAssetBrowserOpen || aiDialogOpen || isDiffViewerOpen || isTitleEditing}
                            hideVoiceButton
                        />
                    </div>
                </div>
            </div>

            {/* Always-visible Bottom Bar */}
            <div className="px-4 py-3 pb-safe border-t border-gray-100 flex items-center justify-between gap-3 bg-gray-50/50 shrink-0 relative">

                {/* Cancel / Finish — float ABOVE bar, bar stays fully visible */}
                {(isVoiceActive || isTranscribing) && (
                    <div className="absolute bottom-full left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] animate-in slide-in-from-bottom-1 fade-in duration-150 z-50">
                        {/* Top row: Cancel / Timer / Finish */}
                        <div className="px-4 py-2.5 flex items-center gap-3">
                            <button
                                onClick={handleCancelVoice}
                                onPointerDown={(e) => e.preventDefault()}
                                disabled={isTranscribing}
                                className="flex-1 border border-red-200 text-red-600 bg-white py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5 active:bg-red-50 disabled:opacity-40 transition-colors"
                            >
                                <X className="w-4 h-4" />
                                Cancel
                            </button>
                            <div className="flex items-center gap-1.5 text-sm font-medium text-orange-500 shrink-0">
                                {isTranscribing ? (
                                    <div className="animate-spin h-3.5 w-3.5 border-2 border-orange-500 border-t-transparent rounded-full" />
                                ) : (
                                    <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                                )}
                                <span className="font-mono">{elapsedSeconds.toFixed(1)}s</span>
                            </div>
                            <button
                                onClick={handleFinishVoice}
                                onPointerDown={(e) => e.preventDefault()}
                                disabled={isTranscribing}
                                className="flex-1 bg-emerald-600 text-white py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5 active:bg-emerald-700 disabled:opacity-40 transition-colors shadow-sm"
                            >
                                <Check className="w-4 h-4" />
                                Finish
                            </button>
                        </div>
                        {/* Bottom row: 90s limit hint */}
                        {!isTranscribing && (
                            <div className="px-4 pb-2.5 pt-0">
                                <div className={`text-xs text-center ${remainingSeconds <= 10 && remainingSeconds > 0 ? 'text-red-500 font-medium animate-pulse' : 'text-gray-400'}`}>
                                    Max {MAX_RECORDING_SECONDS}s recording allowed ({remainingSeconds.toFixed(1)}s left)
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Left: Keyboard / Delete / Return buttons */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => {
                            const next = !isKeyboardEnabled;
                            setIsKeyboardEnabled(next);
                            if (next && editorInstance && !isChatOpen) {
                                setTimeout(() => editorInstance.commands.focus(), 50);
                            } else if (!next && editorInstance) {
                                editorInstance.commands.blur();
                            }
                        }}
                        disabled={isVoiceActive || isTranscribing}
                        className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all shrink-0 ${
                            isKeyboardEnabled
                                ? 'bg-emerald-600 text-white shadow-md'
                                : 'bg-white text-gray-500 border border-gray-200 shadow-sm'
                        } disabled:opacity-40 disabled:cursor-not-allowed`}
                        title={isKeyboardEnabled ? 'Hide keyboard' : 'Show keyboard'}
                    >
                        {isKeyboardEnabled ? <Keyboard className="h-5 w-5" /> : <KeyboardOff className="h-5 w-5" />}
                    </button>
                    {!isKeyboardEnabled && (
                        <>
                            {/* Backspace — supports key-repeat on hold */}
                            <button
                                {...(!(isVoiceActive || isTranscribing) ? getKeyRepeatHandlers(handleDeleteInEditor) : {})}
                                disabled={isVoiceActive || isTranscribing}
                                className="w-11 h-11 rounded-xl flex items-center justify-center transition-all active:scale-95 shrink-0 bg-red-50 text-red-500 border border-red-200 shadow-sm active:bg-red-100 active:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                                title="Delete / Backspace"
                            >
                                <Delete className="h-5 w-5" />
                            </button>
                            {/* Space — supports key-repeat on hold */}
                            <button
                                {...(!(isVoiceActive || isTranscribing) ? getKeyRepeatHandlers(handleSpaceInEditor) : {})}
                                disabled={isVoiceActive || isTranscribing}
                                className="w-11 h-11 rounded-xl flex items-center justify-center transition-all active:scale-95 shrink-0 bg-gray-50 text-gray-600 border border-gray-200 shadow-sm active:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                title="Space"
                            >
                                <Space className="h-5 w-5" />
                            </button>
                            {/* Return — supports key-repeat on hold */}
                            <button
                                {...(!(isVoiceActive || isTranscribing) ? getKeyRepeatHandlers(handleReturnInEditor) : {})}
                                disabled={isVoiceActive || isTranscribing}
                                className="w-11 h-11 rounded-xl flex items-center justify-center transition-all active:scale-95 shrink-0 bg-blue-50 text-blue-600 border border-blue-200 shadow-sm active:bg-blue-100 active:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                                title="Return / Enter"
                            >
                                <CornerDownLeft className="h-5 w-5" />
                            </button>
                        </>
                    )}
                </div>

                {/* Center: Mic button */}
                <button
                    onClick={isVoiceActive || isTranscribing ? undefined : handleStartRecording}
                    onPointerDown={(e) => e.preventDefault()} // Prevent stealing focus from editor/chat
                    disabled={isTranscribing}
                    className={`flex-1 h-11 rounded-xl flex items-center justify-center gap-2 font-medium text-sm transition-all shadow-md ${
                        isTranscribing
                            ? 'bg-indigo-600 text-white cursor-wait'
                            : isVoiceActive
                                ? 'bg-orange-500 text-white'
                                : isChatOpen
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-emerald-600 text-white'
                    }`}
                >
                    {isTranscribing ? (
                        <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                    ) : (
                        <MicrophoneIcon className={`h-5 w-5 ${isVoiceActive ? 'animate-pulse' : ''}`} />
                    )}
                    <span className="truncate">
                        {isTranscribing ? 'Processing...' : isVoiceActive ? 'Listening...' : isChatOpen ? 'Voice to chat' : 'Tap to Talk'}
                    </span>
                </button>

                {/* Right: Chat toggle */}
                <button
                    onClick={() => setIsChatOpen(!isChatOpen)}
                    disabled={isVoiceActive || isTranscribing}
                    className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all shrink-0 disabled:opacity-40 disabled:cursor-not-allowed ${
                        isChatOpen
                            ? 'bg-gray-200 text-gray-700 shadow-sm'
                            : chatError
                                ? 'bg-red-500 text-white animate-pulse shadow-lg'
                                : isChatLoading
                                    ? 'bg-blue-500 text-white animate-pulse'
                                    : 'bg-blue-600 text-white shadow-lg'
                    }`}
                    title={isChatOpen ? 'Back to editor' : 'AI Chat'}
                >
                    {isChatOpen ? (
                        <X className="h-5 w-5" />
                    ) : isChatLoading ? (
                        <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                    ) : (
                        <ChatBubbleLeftRightIcon className="h-5 w-5" />
                    )}
                </button>
            </div>

            {/* Fixed Menu Overlays */}
            {activeMenu && (
                <div
                    className="fixed z-[60] bg-white shadow-xl rounded-lg p-2 flex flex-col gap-1 border border-gray-200 animate-in fade-in zoom-in-95 min-w-[140px]"
                    style={{
                        right: '52px',
                        top: activeMenu.rect.top,
                        marginTop: '-20px' // Slight offset for better alignment
                    }}
                >
                    {/* Backdrop to close */}
                    <div className="fixed inset-0 z-[-1]" onClick={() => setActiveMenu(null)} />

                    {activeMenu.type === 'type' && (
                        <>
                            <button onClick={() => { editorInstance?.chain().focus().setParagraph().run(); setActiveMenu(null); }} className={`p-2 rounded hover:bg-gray-100 flex items-center gap-3 ${editorInstance?.isActive('paragraph') ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}><Pilcrow className="h-4 w-4" /> <span className="text-sm font-medium">Text</span></button>
                            <button onClick={() => { editorInstance?.chain().focus().toggleHeading({ level: 1 }).run(); setActiveMenu(null); }} className={`p-2 rounded hover:bg-gray-100 flex items-center gap-3 ${editorInstance?.isActive('heading', { level: 1 }) ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}><Heading1 className="h-4 w-4" /> <span className="text-sm font-medium">Heading 1</span></button>
                            <button onClick={() => { editorInstance?.chain().focus().toggleHeading({ level: 2 }).run(); setActiveMenu(null); }} className={`p-2 rounded hover:bg-gray-100 flex items-center gap-3 ${editorInstance?.isActive('heading', { level: 2 }) ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}><Heading2 className="h-4 w-4" /> <span className="text-sm font-medium">Heading 2</span></button>
                            <button onClick={() => { editorInstance?.chain().focus().toggleHeading({ level: 3 }).run(); setActiveMenu(null); }} className={`p-2 rounded hover:bg-gray-100 flex items-center gap-3 ${editorInstance?.isActive('heading', { level: 3 }) ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}><Heading3 className="h-4 w-4" /> <span className="text-sm font-medium">Heading 3</span></button>
                        </>
                    )}

                    {activeMenu.type === 'format' && (
                        <>
                            <button onClick={() => { editorInstance?.chain().focus().toggleBold().run(); setActiveMenu(null); }} className={`p-2 rounded hover:bg-gray-100 flex items-center gap-3 ${editorInstance?.isActive('bold') ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}><Bold className="h-4 w-4" /> <span className="text-sm font-medium">Bold</span></button>
                            <button onClick={() => { editorInstance?.chain().focus().toggleItalic().run(); setActiveMenu(null); }} className={`p-2 rounded hover:bg-gray-100 flex items-center gap-3 ${editorInstance?.isActive('italic') ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}><Italic className="h-4 w-4" /> <span className="text-sm font-medium">Italic</span></button>
                            <button onClick={() => { editorInstance?.chain().focus().toggleUnderline().run(); setActiveMenu(null); }} className={`p-2 rounded hover:bg-gray-100 flex items-center gap-3 ${editorInstance?.isActive('underline') ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}><Underline className="h-4 w-4" /> <span className="text-sm font-medium">Underline</span></button>
                            <button onClick={() => { editorInstance?.chain().focus().toggleStrike().run(); setActiveMenu(null); }} className={`p-2 rounded hover:bg-gray-100 flex items-center gap-3 ${editorInstance?.isActive('strike') ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}><Strikethrough className="h-4 w-4" /> <span className="text-sm font-medium">Strike</span></button>
                        </>
                    )}

                    {activeMenu.type === 'list' && (
                        <>
                            <button onClick={() => { editorInstance?.chain().focus().toggleBulletList().run(); setActiveMenu(null); }} className={`p-2 rounded hover:bg-gray-100 flex items-center gap-3 ${editorInstance?.isActive('bulletList') ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}><List className="h-4 w-4" /> <span className="text-sm font-medium">Bullet List</span></button>
                            <button onClick={() => { editorInstance?.chain().focus().toggleOrderedList().run(); setActiveMenu(null); }} className={`p-2 rounded hover:bg-gray-100 flex items-center gap-3 ${editorInstance?.isActive('orderedList') ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}><ListOrdered className="h-4 w-4" /> <span className="text-sm font-medium">Ordered List</span></button>
                            <button onClick={() => { editorInstance?.chain().focus().toggleTaskList().run(); setActiveMenu(null); }} className={`p-2 rounded hover:bg-gray-100 flex items-center gap-3 ${editorInstance?.isActive('taskList') ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}><CheckSquare className="h-4 w-4" /> <span className="text-sm font-medium">Task List</span></button>
                        </>
                    )}

                    {activeMenu.type === 'more' && (
                        <>
                            {/* Link */}
                            <button onClick={() => { handleOpenLinkDialog(); setActiveMenu(null); }} className={`p-2 rounded hover:bg-gray-100 flex items-center gap-3 ${editorInstance?.isActive('link') ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}><LinkIcon className="h-4 w-4" /> <span className="text-sm font-medium">Link</span></button>

                            {/* Image */}
                            <button onClick={() => { setIsAssetBrowserOpen(true); setActiveMenu(null); }} className="p-2 rounded hover:bg-gray-100 flex items-center gap-3 text-gray-700"><ImageIcon className="h-4 w-4" /> <span className="text-sm font-medium">Image</span></button>

                            {/* Table */}
                            <button onClick={() => { editorInstance?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(); setActiveMenu(null); }} className={`p-2 rounded hover:bg-gray-100 flex items-center gap-3 ${editorInstance?.isActive('table') ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}><TableIcon className="h-4 w-4" /> <span className="text-sm font-medium">Table</span></button>

                            {/* Quote */}
                            {/* Quote */}
                            <button onClick={() => { editorInstance?.chain().focus().toggleBlockquote().run(); setActiveMenu(null); }} className={`p-2 rounded hover:bg-gray-100 flex items-center gap-3 ${editorInstance?.isActive('blockquote') ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}><Quote className="h-4 w-4" /> <span className="text-sm font-medium">Quote</span></button>

                            {/* Code */}
                            <button onClick={() => { editorInstance?.chain().focus().toggleCode().run(); setActiveMenu(null); }} className={`p-2 rounded hover:bg-gray-100 flex items-center gap-3 ${editorInstance?.isActive('code') ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}><Code className="h-4 w-4" /> <span className="text-sm font-medium">Code</span></button>
                        </>
                    )}
                </div>
            )}
            {/* Diff Viewer Modal */}
            <DiffViewer
                isOpen={isDiffViewerOpen}
                onClose={() => setIsDiffViewerOpen(false)}
                oldContent={previousContent || ''}
                newContent={editorInstance ? (getFormattedContent(editorInstance, 'markdown') as string || '') : ''}
            />


            <AssetBrowserModal
                isOpen={isAssetBrowserOpen}
                onClose={() => setIsAssetBrowserOpen(false)}
                onSelectAsset={handleAssetSelect}
            />

            <LinkDialog
                isOpen={isLinkDialogOpen}
                onClose={() => setIsLinkDialogOpen(false)}
                initialUrl={linkData.url}
                initialText={linkData.text}
                isEditing={linkData.isEditing}
                onSave={handleSaveLink}
                onRemove={handleRemoveLink}
                disableAutoFocus
            />


            <AICommandDialog
                isOpen={aiDialogOpen}
                onClose={closeAiCommandDialog}
                onSubmit={handleAiCommandSubmit}
                onAccept={handleAcceptResult}
                commandType={aiCommandType}
                initialContent={typeof initialContent === 'string' ? initialContent : ''}
                hasSelection={!!appliedEditorRef?.current?.getSelectionContext()?.text}
                selectedText={appliedEditorRef?.current?.getSelectionContext()?.text || ''}
                aiService={aiService}
                articleId={articleId}
                articleTitle={articleTitle}
            />


            {/* Title Edit Dialog - Bottom Sheet Style */}
            {isTitleEditing && (
                <div className="absolute inset-0 z-[100] flex items-end justify-center">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/40 animate-in fade-in duration-200"
                        onClick={() => setIsTitleEditing(false)}
                    />

                    {/* Sheet Panel */}
                    <div className="relative w-full bg-white rounded-t-2xl shadow-2xl flex flex-col animate-in slide-in-from-bottom duration-300 max-h-[80%]">
                        <div className="flex items-center justify-between p-4 border-b border-gray-100">
                            <h2 className="text-lg font-semibold text-gray-900">Edit Title</h2>
                            <button
                                onClick={() => setIsTitleEditing(false)}
                                className="p-2 text-gray-500 hover:text-gray-900 bg-gray-100 rounded-full"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="p-4 flex-1 overflow-y-auto">
                            <textarea
                                value={tempTitle}
                                onChange={(e) => setTempTitle(e.target.value)}
                                className="w-full min-h-[120px] p-0 text-xl font-medium border-none focus:ring-0 resize-none outline-none placeholder:text-gray-400 bg-transparent leading-relaxed text-gray-900"
                                placeholder="Enter article title..."
                                autoFocus
                            />
                        </div>
                        <div className="p-4 border-t border-gray-100 bg-gray-50/50 pb-safe">
                            <button
                                onClick={() => {
                                    if (onTitleChange) onTitleChange(tempTitle);
                                    setIsTitleEditing(false);
                                }}
                                className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold text-lg hover:bg-blue-700 active:bg-blue-800 transition-all transform active:scale-[0.98] shadow-sm"
                            >
                                Save Title
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
